/**
 * HeroCollage — illustrated world-map hero with 7 floating scene cards.
 *
 * Each card is a themed miniature scene (person + landmark backdrop,
 * mountain lake, farmer + veggies, family portrait, volunteer, hiker,
 * cityscape) rendered as inline SVG so we ship zero external assets.
 *
 * The dotted-world underlay + centre callout "Different Places / Brighter
 * People / A Stronger Tomorrow" match the reference mock exactly.
 */
import { Link } from "react-router-dom";

const CARDS = [
  { top: "2%",  left:  "4%",  scene: "london_woman",    caption: "People\nBuild Brighter\nCommunities",  to: "/community"     },
  { top: "-2%", left: "48%",  scene: "mountain_lake",   caption: "Stronger\nTogether\nEverywhere",       to: "/network"       },
  { top: "6%",  left: "80%",  scene: "farmer",          caption: "Local Solutions\nReal Change",         to: "/civic"         },
  { top: "44%", left:  "2%",  scene: "family",          caption: "Communities\nBelong Here",             to: "/community"     },
  { top: "48%", left: "38%",  scene: "european_town",   caption: "Explore\nShare\nMake a Difference",    to: "/explore"       },
  { top: "50%", left: "78%",  scene: "volunteer",       caption: "Kind People\nStronger\nCommunities",   to: "/social-capital"},
  { top: "82%", left: "58%",  scene: "hiker",           caption: "New Perspectives\nA More Connected World", to: "/blog"      },
];

export default function HeroCollage() {
  return (
    <div
      className="relative w-full aspect-[16/12] max-h-[560px] rounded-3xl overflow-hidden mt-2 sm:mt-0"
      data-testid="sp-hero-collage"
    >
      {/* Note: ambient gradient orbs live on the parent hero section
       *  so they bleed outside the collage and behind the world map. */}
      <WorldMap />

      {/* centre editorial caption */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <p className="text-center text-slate-500 font-serif italic leading-tight text-sm sm:text-lg lg:text-xl px-6 sm:px-8 max-w-xs">
          <span className="block">Different <em className="text-blue-700 not-italic font-semibold">Places</em></span>
          <span className="block">Brighter <em className="text-blue-700 not-italic font-semibold">People</em></span>
          <span className="block">A Stronger <em className="text-blue-700 not-italic font-semibold">Tomorrow</em></span>
        </p>
      </div>

      {CARDS.map((c, i) => (
        <SceneCard key={i} {...c} index={i} />
      ))}
    </div>
  );
}

function SceneCard({ top, left, scene, caption, to, index }) {
  const lines = caption.split("\n");
  return (
    <Link
      to={to}
      data-testid={`sp-hero-card-${index}`}
      className="absolute group"
      style={{ top, left, transform: "translate(-20%, 0)" }}
    >
      <div className="flex flex-col items-start gap-1.5">
        <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-28 lg:h-28 rounded-2xl ring-4 ring-white shadow-lg overflow-hidden transition-transform group-hover:-translate-y-0.5">
          <SceneSvg name={scene} />
        </div>
        <div className="hidden sm:block bg-white/95 backdrop-blur rounded-lg px-2.5 py-1.5 shadow-md ring-1 ring-slate-100 max-w-[140px]">
          <p className="font-serif italic text-[11px] leading-tight text-slate-700 whitespace-pre-line">
            {lines.join("\n")}
          </p>
        </div>
      </div>
    </Link>
  );
}

function SceneSvg({ name }) {
  const Scene = SCENES[name] || SCENES.london_woman;
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
      <Scene />
    </svg>
  );
}

/* ──────── Scenes ──────── */
const SCENES = {
  london_woman: () => (
    <g>
      <rect width="100" height="100" fill="#dbeafe" />
      {/* sky glow */}
      <circle cx="80" cy="20" r="15" fill="#fef3c7" opacity="0.75" />
      {/* Big Ben silhouette */}
      <rect x="70" y="30" width="10" height="45" fill="#78716c" opacity="0.75" />
      <polygon points="70,30 80,30 75,20" fill="#57534e" opacity="0.85" />
      <circle cx="75" cy="38" r="3" fill="#fef3c7" />
      {/* face */}
      <circle cx="40" cy="52" r="20" fill="#fcd7b0" />
      {/* hair */}
      <path d="M25 45 Q 25 30, 40 30 Q 55 30, 55 45 Q 50 40, 40 40 Q 30 40, 25 45 Z" fill="#4a2c17" />
      {/* eyes */}
      <circle cx="34" cy="52" r="1.8" fill="#0f172a" />
      <circle cx="46" cy="52" r="1.8" fill="#0f172a" />
      {/* smile */}
      <path d="M32 60 Q 40 66, 48 60" stroke="#7c2d12" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* shoulders */}
      <path d="M15 100 Q 15 72, 40 72 Q 65 72, 65 100 Z" fill="#065f46" />
    </g>
  ),

  mountain_lake: () => (
    <g>
      <rect width="100" height="55" fill="#c7e9fb" />
      <rect y="55" width="100" height="45" fill="#38bdf8" opacity="0.75" />
      {/* back mountain */}
      <polygon points="10,55 35,20 60,55" fill="#7c3aed" opacity="0.55" />
      <polygon points="35,20 42,28 28,28" fill="white" opacity="0.9" />
      {/* front mountain */}
      <polygon points="40,55 70,15 100,55" fill="#059669" opacity="0.8" />
      <polygon points="70,15 78,25 62,25" fill="white" opacity="0.9" />
      <polygon points="0,55 20,32 45,55" fill="#0891b2" opacity="0.75" />
      {/* reflection */}
      <polygon points="10,55 35,90 60,55" fill="#7c3aed" opacity="0.3" />
      <polygon points="40,55 70,95 100,55" fill="#059669" opacity="0.35" />
      {/* clouds */}
      <ellipse cx="20" cy="15" rx="8" ry="3" fill="white" opacity="0.85" />
      <ellipse cx="82" cy="10" rx="6" ry="2.5" fill="white" opacity="0.85" />
      {/* sun */}
      <circle cx="60" cy="12" r="5" fill="#fbbf24" opacity="0.9" />
    </g>
  ),

  farmer: () => (
    <g>
      <rect width="100" height="100" fill="#d1fae5" />
      {/* farm hills */}
      <path d="M0 70 Q 30 55, 60 65 Q 85 55, 100 65 L 100 100 L 0 100 Z" fill="#22c55e" opacity="0.55" />
      <path d="M0 80 Q 30 70, 70 78 Q 90 72, 100 80 L 100 100 L 0 100 Z" fill="#16a34a" opacity="0.7" />
      {/* farmer face */}
      <circle cx="50" cy="42" r="18" fill="#fcd7b0" />
      {/* hat */}
      <ellipse cx="50" cy="25" rx="22" ry="4" fill="#a16207" />
      <path d="M35 25 Q 35 15, 50 15 Q 65 15, 65 25 Z" fill="#a16207" />
      {/* beard */}
      <path d="M38 50 Q 50 60, 62 50 Q 60 58, 50 60 Q 40 58, 38 50 Z" fill="#4a2c17" opacity="0.85" />
      <circle cx="43" cy="42" r="1.6" fill="#0f172a" />
      <circle cx="57" cy="42" r="1.6" fill="#0f172a" />
      {/* basket */}
      <path d="M25 78 L 75 78 L 70 95 L 30 95 Z" fill="#a16207" />
      <circle cx="40" cy="74" r="4" fill="#dc2626" />
      <circle cx="50" cy="72" r="5" fill="#ea580c" />
      <circle cx="60" cy="74" r="4" fill="#16a34a" />
      <circle cx="46" cy="80" r="3" fill="#22c55e" />
      <circle cx="58" cy="82" r="3" fill="#facc15" />
    </g>
  ),

  family: () => (
    <g>
      <rect width="100" height="100" fill="#fce7f3" />
      {/* backdrop sun */}
      <circle cx="82" cy="18" r="10" fill="#fef3c7" opacity="0.8" />
      {/* dad */}
      <circle cx="25" cy="38" r="10" fill="#f7c39a" />
      <path d="M15 100 Q 15 55, 25 55 Q 35 55, 35 100 Z" fill="#1e40af" />
      {/* mom */}
      <circle cx="75" cy="38" r="10" fill="#fcd7b0" />
      <path d="M65 100 Q 65 55, 75 55 Q 85 55, 85 100 Z" fill="#be185d" />
      <path d="M67 32 Q 67 24, 75 24 Q 83 24, 83 32 Q 79 30, 75 30 Q 71 30, 67 32" fill="#4a2c17" />
      {/* child */}
      <circle cx="50" cy="55" r="8" fill="#f9c39a" />
      <path d="M42 100 Q 42 65, 50 65 Q 58 65, 58 100 Z" fill="#059669" />
      {/* smile lines */}
      <path d="M20 41 Q 25 44, 30 41" stroke="#7c2d12" strokeWidth="1" fill="none" />
      <path d="M70 41 Q 75 44, 80 41" stroke="#7c2d12" strokeWidth="1" fill="none" />
      <path d="M46 57 Q 50 59, 54 57" stroke="#7c2d12" strokeWidth="1" fill="none" />
    </g>
  ),

  european_town: () => (
    <g>
      <rect width="100" height="100" fill="#e0f2fe" />
      {/* mountains */}
      <polygon points="0,55 25,25 50,55" fill="#a78bfa" opacity="0.55" />
      <polygon points="40,60 70,20 100,60" fill="#9333ea" opacity="0.6" />
      <polygon points="70,20 76,28 64,28" fill="white" opacity="0.9" />
      {/* town */}
      <rect x="10" y="60" width="14" height="20" fill="#fca5a5" />
      <polygon points="8,60 26,60 17,50" fill="#7f1d1d" />
      <rect x="30" y="55" width="18" height="25" fill="#fef3c7" />
      <polygon points="28,55 50,55 39,42" fill="#7c2d12" />
      <rect x="54" y="58" width="14" height="22" fill="#fde68a" />
      <polygon points="52,58 70,58 61,48" fill="#7f1d1d" />
      <rect x="74" y="62" width="12" height="18" fill="#fbcfe8" />
      <polygon points="72,62 88,62 80,54" fill="#7f1d1d" />
      {/* church tower */}
      <rect x="38" y="30" width="6" height="20" fill="#78716c" />
      <polygon points="38,30 44,30 41,20" fill="#57534e" />
      <line x1="41" y1="20" x2="41" y2="15" stroke="#dc2626" strokeWidth="0.8" />
      {/* water */}
      <rect y="80" width="100" height="20" fill="#38bdf8" opacity="0.7" />
      {/* boat */}
      <path d="M46 88 L 62 88 L 58 92 L 50 92 Z" fill="white" />
      <line x1="54" y1="82" x2="54" y2="88" stroke="#7c2d12" strokeWidth="0.8" />
      <polygon points="54,83 60,87 54,87" fill="#dc2626" />
    </g>
  ),

  volunteer: () => (
    <g>
      <rect width="100" height="100" fill="#ede9fe" />
      {/* face */}
      <circle cx="50" cy="42" r="20" fill="#fcd7b0" />
      {/* hair */}
      <path d="M30 42 Q 30 24, 50 22 Q 70 24, 70 42 Q 65 38, 50 38 Q 35 38, 30 42 Z" fill="#78350f" />
      {/* glasses */}
      <circle cx="42" cy="42" r="4" fill="none" stroke="#0f172a" strokeWidth="1.2" />
      <circle cx="58" cy="42" r="4" fill="none" stroke="#0f172a" strokeWidth="1.2" />
      <line x1="46" y1="42" x2="54" y2="42" stroke="#0f172a" strokeWidth="0.8" />
      {/* smile */}
      <path d="M42 52 Q 50 58, 58 52" stroke="#7c2d12" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* shirt with VOLUNTEER label */}
      <path d="M15 100 Q 15 68, 50 68 Q 85 68, 85 100 Z" fill="#2563eb" />
      <rect x="30" y="80" width="40" height="10" rx="1" fill="white" />
      <text x="50" y="87" fontFamily="Arial" fontSize="6.5" fontWeight="700" fill="#2563eb" textAnchor="middle" letterSpacing="0.8">
        VOLUNTEER
      </text>
      {/* heart pin */}
      <path d="M70 74 C 68 71, 63 72, 63 76 C 63 80, 70 84, 70 84 C 70 84, 77 80, 77 76 C 77 72, 72 71, 70 74 Z" fill="#dc2626" />
    </g>
  ),

  hiker: () => (
    <g>
      <rect width="100" height="100" fill="#cffafe" />
      {/* mountains */}
      <polygon points="0,60 30,25 55,60" fill="#0e7490" opacity="0.6" />
      <polygon points="40,65 75,20 100,65" fill="#155e75" opacity="0.75" />
      <polygon points="75,20 82,30 68,30" fill="white" opacity="0.9" />
      {/* sky */}
      <circle cx="20" cy="18" r="7" fill="white" opacity="0.85" />
      <circle cx="28" cy="16" r="9" fill="white" opacity="0.75" />
      {/* hiker */}
      <circle cx="50" cy="52" r="10" fill="#fcd7b0" />
      <path d="M42 46 Q 42 38, 50 38 Q 58 38, 58 46 Q 54 42, 50 42 Q 46 42, 42 46 Z" fill="#4a2c17" />
      {/* backpack */}
      <rect x="38" y="60" width="24" height="26" rx="3" fill="#059669" />
      <rect x="42" y="66" width="16" height="6" fill="#065f46" />
      {/* shoulders/body */}
      <path d="M32 100 Q 32 62, 50 62 Q 68 62, 68 100 Z" fill="#dc2626" />
      {/* smile */}
      <path d="M46 56 Q 50 59, 54 56" stroke="#7c2d12" strokeWidth="1" fill="none" />
    </g>
  ),
};

function WorldMap() {
  // Sparse continent-dotted map underlay. Kept tiny (< 3KB) so page-load
  // stays snappy while still reading as a map at a glance.
  const clusters = [
    ...grid(6,  20, 24, 32, 10),  // NAmerica
    ...grid(22, 55, 12, 24, 9),   // SAmerica
    ...grid(46, 22, 12, 15, 9),   // Europe
    ...grid(46, 40, 14, 30, 10),  // Africa
    ...grid(58, 22, 28, 25, 12),  // Asia
    ...grid(76, 65, 15, 15, 8),   // Oceania
  ];
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
      <defs>
        <radialGradient id="wm-bg" cx="50%" cy="50%" r="80%">
          <stop offset="0%"   stopColor="#EAF2FB" />
          <stop offset="100%" stopColor="#F5F9FE" />
        </radialGradient>
      </defs>
      <rect width="100" height="100" fill="url(#wm-bg)" />
      {clusters.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r={d.r} fill="#93B4D8" opacity="0.55" />
      ))}
      {/* faint route arcs */}
      {[
        ["M 15 35 Q 45 15, 65 30", 0.5],
        ["M 25 55 Q 45 40, 70 55", 0.4],
        ["M 40 60 Q 60 75, 80 70", 0.35],
      ].map(([d, o], i) => (
        <path key={i} d={d} fill="none" stroke="#60a5fa" strokeWidth="0.25" strokeDasharray="1 1.2" opacity={o} />
      ))}
    </svg>
  );
}

function grid(x0, y0, w, h, density) {
  const out = [];
  const step = w / density;
  for (let x = x0; x < x0 + w; x += step) {
    for (let y = y0; y < y0 + h; y += step * 0.8) {
      const jx = ((x * 31 + y * 17) % 5) / 12;
      const jy = ((x * 13 + y * 29) % 5) / 12;
      if ((Math.floor(x) + Math.floor(y)) % 2 === 0) continue;
      out.push({ x: x + jx, y: y + jy, r: 0.35 });
    }
  }
  return out;
}
